import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { baseUrl } from 'src/environments/environment';
import {
  FullPatient,
  IFinglodineSubscriptionData,
  ILcaPatientDTO,
  IPspPatientDetailsDTO,
  IPspPatientDTO,
  Patient,
  PatientAddress,
  PatientSubscription,
  PatientUpdated,
} from '../_models/patient.model';
import { Pagination, Response } from 'src/app/_models/response.model';
import { PatientScreenTypeEnum } from '../enums/patient-screen-enum';
import { IExcelPatient } from '../_interfaces/patients/i-excel-patient';

@Injectable({
  providedIn: 'root',
})
export class PatientService {
  private emergencyPatientUrl = 'api/PatientsEmergency';
  private filteredPatientUrl = 'api/AdminPatients/';
  private addressUrl = 'api/AdminPatientAddresses/';
  private subscriptionUrl = 'api/AdminPspSubscriptions/';
  addMedicationRequestUrl: string = 'api/AdminPspMedicationRequests/AddMedicationRequest';
  getSubscriptionDocumentsUrl: string = 'api/AdminPspSubscriptions/GetSubscriptionDocuments';
  uploadSubscriptionDocumentsUrl: string = 'api/AdminPspSubscriptions/UploadSubscriptionDocuments';
  isMedicalTests = new BehaviorSubject(false);
  isPspSection = new BehaviorSubject(false);
  isApprovals = new BehaviorSubject(false);
  patientAddresses: PatientAddress[];
  patient: FullPatient;
  selectedSubscription: PatientSubscription;
  finglodineSubscription: IFinglodineSubscriptionData
  filteredPspPatientsUrl: string = 'api/AdminPatients/GetAllPspPatients';
  exportToExcelPatientsUrl: string = 'api/AdminPatients/GetAllExcelPatients';
  exportToExcelPspPatientsUrl: string = 'api/AdminPatients/GetAllExcelPspPatients'
  exportToExcelLcaPatientsUrl: string = 'api/AdminLca/GetAllExcelLcaPatients'
  finglodineSubscriptionUrl: string = 'api/PSPSubscription/'



  private patientScreenType = new BehaviorSubject<PatientScreenTypeEnum>(null);
  private patientScreenType$ = this.patientScreenType.asObservable();

  constructor(private http: HttpClient) { }

  getAllEmergencyPatients(): Observable<any> {
    return this.http.get(
      this.emergencyPatientUrl + '/GetAdminPatientsEmergency'
    );
  }

  getExportToExcelPatients(): Observable<Response<IExcelPatient[]>> {
    return this.http.get<Response<IExcelPatient[]>>(this.exportToExcelPatientsUrl);
  }

  getExportToExcelPspPatients(companyId): Observable<Response<IPspPatientDTO[]>> {
    let params = new HttpParams().set('companyId', companyId.toString())
    return this.http.get<Response<IPspPatientDTO[]>>(this.exportToExcelPspPatientsUrl, { params });
  }

  getExportToExcelLcaPatients(): Observable<Response<ILcaPatientDTO[]>> {
    return this.http.get<Response<ILcaPatientDTO[]>>(this.exportToExcelLcaPatientsUrl);
  }

  getFilteredPatients(
    params
  ): Observable<Response<{ items: Patient[]; pagination: Pagination }>> {
    return this.http.get<
      Response<{ items: Patient[]; pagination: Pagination }>
    >(this.filteredPatientUrl + 'GetFilteredPatients', { params });
  }

  getPateintById(id): Observable<Response<FullPatient>> {
    return this.http.get<Response<FullPatient>>(
      this.filteredPatientUrl + 'GetPatientDetails/' + id
    );
  }

  addAddress(address): Observable<Response<PatientAddress>> {
    return this.http.post<Response<PatientAddress>>(
      this.addressUrl + 'AddPatientAddress/',
      address
    );
  }

  updateAddress(address: PatientAddress): Observable<Response<PatientAddress>> {
    return this.http.put<Response<PatientAddress>>(
      this.addressUrl + 'EditPatientAddress',
      address
    );
  }

  deleteAddress(id: number): Observable<Response<{ flag: boolean }>> {
    return this.http.put<Response<{ flag: boolean }>>(
      this.addressUrl + 'DeletePatientAddress/',
      { id }
    );
  }

  getPatientPspSubscriptions(patientId: number): Observable<
    Response<{
      subscriptions: PatientSubscription[];
      isHaveCareGiverSubscription: boolean;
    }>
  > {
    return this.http.get<
      Response<{
        subscriptions: PatientSubscription[];
        isHaveCareGiverSubscription: boolean;
      }>
    >(this.subscriptionUrl + 'GePatientPspSubscriptions', {
      params: { patientId: patientId.toString() },
    });
  }

  addMedicationRequest(body): Observable<Response<{ otp: string }>> {
    return this.http.post<Response<{ otp: string }>>(
      this.addMedicationRequestUrl,
      body
    );
  }

  searchByName(patientName) {
    return this.http.get(
      `${this.emergencyPatientUrl}/SearchByName/${patientName}`
    );
  }

  updatePatientDetails(patientData: PatientUpdated): Observable<Response<{ flag: boolean }>> {
    return this.http.put<Response<{ flag: boolean }>>(this.filteredPatientUrl + 'UpdatePatientDetails',
      patientData);
  }

  getPatientScreenType(): Observable<PatientScreenTypeEnum> {
    return this.patientScreenType$;
  }

  setPatientScreenType(patientScreenType: PatientScreenTypeEnum) {
    this.patientScreenType.next(patientScreenType);
  }

  getFilteredPspPatients(
    params
  ): Observable<Response<{ items: IPspPatientDTO[]; pagination: Pagination }>> {
    return this.http.get<
      Response<{ items: IPspPatientDTO[]; pagination: Pagination }>
    >(this.filteredPspPatientsUrl, { params });
  }

  getPspPatientDetails(id): Observable<Response<IPspPatientDetailsDTO>> {
    return this.http.get<Response<IPspPatientDetailsDTO>>(
      this.filteredPatientUrl + 'GetPspPatientDetails/' + id
    );
  }

  getFinglodineSubscriptionData(subscriptionId): Observable<Response<IFinglodineSubscriptionData>> {
    return this.http.get<Response<IFinglodineSubscriptionData>>(
      this.finglodineSubscriptionUrl + subscriptionId
    )
  }

  getSubscriptionDocuments(subscriptionId: number): Observable<Response<{ urls: string[] }>> {
    return this.http.get<Response<{ urls: string[] }>>
            (this.getSubscriptionDocumentsUrl+`?subscriptionId=${subscriptionId}`
    );
  }

  uploadSubscriptionDocuments(uploadedDocuments: any): Observable<Response<{ flag: boolean }>> {
    return this.http.post<Response<{ flag: boolean }>>
            (this.uploadSubscriptionDocumentsUrl, uploadedDocuments
    );
  }
}
